// coordGrid.js — MGRS / UTM / WGS84 coordinate grid GeoJSON generator + OL layer helpers.
//
// MGRS branch (ticket 05): the MGRS grid used to be a lon/lat-stepped graticule dressed up with
// MGRS-style labels, which visibly diverged from a real MGRS grid near UTM zone boundaries, the
// Norway/Svalbard exceptions, and the poles. Spiked `@zwaarcontrast/ol-graticule` +
// `@zwaarcontrast/ol-graticule-mgrs` (MIT-licensed, purpose-built `MgrsGridSystem` +
// `UniversalGraticule`) against a known-good reference (the `mgrs` npm package already trusted
// elsewhere in this repo, cross-checked against NGA-spec literals for the polar UPS case `mgrs`
// can't compute at all) — see `__tests__/coordGrid.mgrs.test.js`'s first describe block for the
// permanent record of that evaluation. It held up at all three of the ticket's test locations
// (standard, Norway/Svalbard, polar), so it's adopted directly here rather than vendoring the
// zone-geometry math. UTM/WGS84 branches below are untouched.

import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import {Stroke, Style} from 'ol/style';
import {fromLonLat, toLonLat} from 'ol/proj';
import {UniversalGraticule} from '@zwaarcontrast/ol-graticule';
import {MgrsGridSystem} from '@zwaarcontrast/ol-graticule-mgrs';

// ── WGS84 graticule ──────────────────────────────────────────────────────────

function wgs84Step(zoom) {
    if (zoom >= 10) return 0.1;
    if (zoom >= 7)  return 0.5;
    if (zoom >= 5)  return 1;
    if (zoom >= 3)  return 5;
    return 10;
}

export function buildWGS84Grid(bounds, zoom) {
    const step = wgs84Step(zoom);
    const {west, south, east, north} = bounds;
    const lines = [];
    const snap = (v, s) => Math.ceil(v / s) * s;

    for (let lon = snap(west, step); lon <= east; lon += step) {
        const lon2 = parseFloat(lon.toFixed(6));
        lines.push({
            type: 'Feature',
            geometry: {type: 'LineString', coordinates: [[lon2, south], [lon2, north]]},
            properties: {gridType: 'wgs84'},
        });
    }
    for (let lat = snap(south, step); lat <= north; lat += step) {
        const lat2 = parseFloat(lat.toFixed(6));
        lines.push({
            type: 'Feature',
            geometry: {type: 'LineString', coordinates: [[west, lat2], [east, lat2]]},
            properties: {gridType: 'wgs84'},
        });
    }
    return {type: 'FeatureCollection', features: lines};
}

// ── UTM graticule ────────────────────────────────────────────────────────────

function utmZoneForLon(lon) {
    return Math.floor((lon + 180) / 6) + 1;
}

export function buildUTMGrid(bounds, zoom) {
    const {west, south, east, north} = bounds;
    const lines = [];

    const startZone = utmZoneForLon(west);
    const endZone   = utmZoneForLon(east);
    for (let z = startZone; z <= endZone; z++) {
        const lon = -180 + (z - 1) * 6;
        if (lon < west || lon > east) continue;
        const lon2 = parseFloat(lon.toFixed(6));
        lines.push({
            type: 'Feature',
            geometry: {type: 'LineString', coordinates: [[lon2, Math.max(south, -80)], [lon2, Math.min(north, 84)]]},
            properties: {gridType: 'utm'},
        });
    }

    const latStep = 8;
    for (let lat = Math.max(-80, Math.ceil(south / latStep) * latStep); lat <= Math.min(84, north); lat += latStep) {
        const lat2 = parseFloat(lat.toFixed(6));
        lines.push({
            type: 'Feature',
            geometry: {type: 'LineString', coordinates: [[west, lat2], [east, lat2]]},
            properties: {gridType: 'utm'},
        });
    }
    return {type: 'FeatureCollection', features: lines};
}

// ── MGRS grid (ticket 05: @zwaarcontrast/ol-graticule + ol-graticule-mgrs) ───
//
// Unlike the UTM/WGS84 branches above, the MGRS grid isn't built as a GeoJSON
// FeatureCollection here — `MgrsGridSystem` computes real UTM grid-zone/100km-square geometry
// (Norway/Svalbard exceptions, UPS polar fallback) and `UniversalGraticule` (an OL VectorLayer
// subclass) renders + labels it itself, refreshing on pan/zoom without any help from
// `updateCoordGrid`/`drawCoordGridLabels`. Those two functions only toggle this layer's
// visibility for the 'mgrs' grid type; see the "OL layer setup" section below.

const MGRS_GRID_LAYER_ID = '__coord-grid-mgrs__';

const MGRS_LINE_STROKE = new Stroke({color: 'rgba(255,255,255,0.55)', width: 0.75, lineDash: [4, 4]});

function buildMGRSLayer() {
    const layer = new UniversalGraticule({
        gridSystem: new MgrsGridSystem(),
        style: {line: MGRS_LINE_STROKE},
        // A worldwide low-zoom view can show ~60 zones × ~10 visible latitude bands × 2 axes,
        // plus the 4 UPS polar zones' 100km grids (1200+ line features) — matches the
        // package's own demo guidance for the world-view case, so legitimate GZD lines don't
        // get silently truncated.
        maxLines: 5000,
    });
    layer.set('id', MGRS_GRID_LAYER_ID);
    layer.setZIndex(10);
    layer.setVisible(false);
    return layer;
}

// ── OL layer setup ───────────────────────────────────────────────────────────

const GRID_LAYER_ID = '__coord-grid__';

const GRID_STYLE = new Style({
    stroke: new Stroke({color: 'rgba(255,255,255,0.55)', width: 0.75, lineDash: [4, 4]}),
});

const FORMAT = new GeoJSON({featureProjection: 'EPSG:3857'});

export function addCoordGridLayers(map) {
    if (map.getLayers().getArray().some((l) => l.get('id') === GRID_LAYER_ID)) return;
    const layer = new VectorLayer({
        source: new VectorSource(),
        style: GRID_STYLE,
        zIndex: 10,
    });
    layer.set('id', GRID_LAYER_ID);
    map.addLayer(layer);
    map.addLayer(buildMGRSLayer());
}

export function removeCoordGridLayers(map) {
    const toRemove = map.getLayers().getArray()
        .filter((l) => l.get('id') === GRID_LAYER_ID || l.get('id') === MGRS_GRID_LAYER_ID);
    toRemove.forEach((l) => map.removeLayer(l));
}

function getGridLayer(map) {
    return map.getLayers().getArray().find((l) => l.get('id') === GRID_LAYER_ID) ?? null;
}

function getMGRSGridLayer(map) {
    return map.getLayers().getArray().find((l) => l.get('id') === MGRS_GRID_LAYER_ID) ?? null;
}

export function updateCoordGrid(map, gridType) {
    const layer = getGridLayer(map);
    const mgrsLayer = getMGRSGridLayer(map);
    if (!layer || !mgrsLayer) return;

    mgrsLayer.setVisible(gridType === 'mgrs');
    if (gridType === 'mgrs') {
        // The MGRS layer renders/refreshes itself; the generic layer just stays empty underneath.
        layer.getSource().clear(true);
        return;
    }

    const view   = map.getView();
    const zoom   = view.getZoom() ?? 4;
    const extent = view.calculateExtent(map.getSize());
    // OL extent is in EPSG:3857; convert to lon/lat for the grid generators.
    const sw = toLonLat([extent[0], extent[1]]);
    const ne = toLonLat([extent[2], extent[3]]);
    const bounds = {west: sw[0], south: sw[1], east: ne[0], north: ne[1]};

    const data = gridType === 'utm' ? buildUTMGrid(bounds, zoom) : buildWGS84Grid(bounds, zoom);

    const source = layer.getSource();
    source.clear(true);
    source.addFeatures(FORMAT.readFeatures(data));
}

// ── Canvas label overlay ──────────────────────────────────────────────────────

function midpointOfLine(coords) {
    return coords[Math.floor(coords.length / 2)];
}

function labelForPoint([lon, lat], gridType) {
    if (gridType === 'utm') {
        const zone = Math.floor((lon + 180) / 6) + 1;
        const band = 'CDEFGHJKLMNPQRSTUVWX'[Math.floor((lat + 80) / 8)];
        return band ? `${zone}${band}` : `${zone}`;
    }
    return lat === 0 || lon === 0 ? '' :
        Math.abs(lat) < 0.001 ? `${lon.toFixed(1)}°` : `${lat.toFixed(1)}°`;
}

/**
 * Draw coordinate grid labels onto a canvas element positioned over the map.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {import('ol/Map').default} map
 * @param {string} gridType  'mgrs' | 'utm' | 'wgs84'
 */
export function drawCoordGridLabels(canvas, map, gridType) {
    if (!canvas || !map) return;

    const {width, height} = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = Math.round(width  * dpr);
    canvas.height = Math.round(height * dpr);

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.scale(dpr, dpr);

    // The MGRS layer (buildMGRSLayer, above) draws its own GZD/100km-square labels directly
    // onto the map's own canvas via UniversalGraticule's postrender hook — this overlay canvas
    // has nothing to add for 'mgrs' beyond staying cleared.
    if (gridType === 'mgrs') return;

    const view   = map.getView();
    const zoom   = view.getZoom() ?? 4;
    const extent = view.calculateExtent(map.getSize());
    const sw = toLonLat([extent[0], extent[1]]);
    const ne = toLonLat([extent[2], extent[3]]);
    const bounds = {west: sw[0], south: sw[1], east: ne[0], north: ne[1]};

    const data = gridType === 'utm' ? buildUTMGrid(bounds, zoom) : buildWGS84Grid(bounds, zoom);

    const stride = zoom >= 10 ? 3 : zoom >= 7 ? 4 : zoom >= 4 ? 5 : 8;

    ctx.font         = `bold ${zoom >= 8 ? 11 : 10}px system-ui, sans-serif`;
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';

    const horizontals = data.features.filter((f) => {
        const c = f.geometry.coordinates;
        return c.length >= 2 && c[0][1] === c[c.length - 1][1];
    });

    horizontals.forEach((f, i) => {
        if (i % stride !== 0) return;
        const mid = midpointOfLine(f.geometry.coordinates);
        const label = labelForPoint(mid, gridType);
        if (!label) return;
        // OL getPixelFromCoordinate returns CSS viewport pixels; canvas ctx is scaled by dpr
        // so CSS pixel coords can be fed directly into ctx calls.
        const px = map.getPixelFromCoordinate(fromLonLat(mid));
        if (!px) return;
        const [px_x, px_y] = px;
        if (px_x < 0 || px_x > width || px_y < 0 || px_y > height) return;

        ctx.strokeStyle = 'rgba(0,0,0,0.65)';
        ctx.lineWidth   = 3;
        ctx.strokeText(label, px_x, px_y);
        ctx.fillStyle   = 'rgba(255,255,255,0.9)';
        ctx.fillText(label, px_x, px_y);
    });
}
